import React, { memo, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { Mail, Bot, User, ChevronDown, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';
import { contactsAPI } from '../../api';

const LogEmailDialog = memo(({ 
  open, 
  onOpenChange, 
  contactId,
  contactEmail,
  teamMembers = [], 
  aiAgents = [],
  onSuccess 
}) => {
  const [form, setForm] = useState({
    subject: '',
    body: '',
    direction: 'outbound'
  });
  const [createFollowUp, setCreateFollowUp] = useState(false);
  const [followUpDetails, setFollowUpDetails] = useState({
    title: '',
    due_date: '',
    assigned_to: '',
    assigned_type: 'user'
  });
  const [loading, setLoading] = useState(false);
  const [followUpOpen, setFollowUpOpen] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!form.subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }

    try {
      setLoading(true);
      
      await contactsAPI.createEmail(contactId, {
        ...form,
        to_email: contactEmail,
        contact_id: contactId
      });
      
      // Create follow-up task if requested
      if (createFollowUp && followUpDetails.title) {
        let assignedToName = '';
        if (followUpDetails.assigned_type === 'ai_agent') {
          const agent = aiAgents.find(a => a.id === followUpDetails.assigned_to);
          assignedToName = agent?.name || agent?.display_name || 'AI Agent';
        } else {
          const member = teamMembers.find(m => m.id === followUpDetails.assigned_to);
          assignedToName = member ? `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email : '';
        }
        
        const taskData = {
          title: followUpDetails.title,
          description: `Follow-up for email: ${form.subject}`,
          priority: 'medium',
          status: 'pending',
          due_date: followUpDetails.due_date ? new Date(followUpDetails.due_date).toISOString() : null,
          assigned_to: followUpDetails.assigned_to || null,
          assigned_to_type: followUpDetails.assigned_type,
          assigned_to_name: assignedToName,
          is_follow_up: true
        };
        await contactsAPI.createTask(contactId, taskData);
        toast.success('Email logged with follow-up task created');
      } else {
        toast.success('Email logged');
      }
      
      // Reset form
      setForm({ subject: '', body: '', direction: 'outbound' });
      setCreateFollowUp(false);
      setFollowUpDetails({ title: '', due_date: '', assigned_to: '', assigned_type: 'user' });
      
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to log email');
    } finally {
      setLoading(false);
    }
  }, [form, contactId, contactEmail, createFollowUp, followUpDetails, teamMembers, aiAgents, onOpenChange, onSuccess]);

  const handleClose = useCallback(() => {
    setForm({ subject: '', body: '', direction: 'outbound' });
    setCreateFollowUp(false);
    setFollowUpDetails({ title: '', due_date: '', assigned_to: '', assigned_type: 'user' });
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-600" />
            Log Email
          </DialogTitle>
          <DialogDescription>
            Record an email interaction with this contact
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Direction</Label>
            <Select 
              value={form.direction} 
              onValueChange={(val) => setForm(prev => ({ ...prev, direction: val }))}
            >
              <SelectTrigger data-testid="email-direction-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="outbound">Sent (Outbound)</SelectItem>
                <SelectItem value="inbound">Received (Inbound)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email-subject">Subject *</Label>
            <Input
              id="email-subject"
              value={form.subject}
              onChange={(e) => setForm(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="Email subject"
              data-testid="email-subject-input"
              autoFocus
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email-body">Content</Label>
            <Textarea
              id="email-body"
              value={form.body}
              onChange={(e) => setForm(prev => ({ ...prev, body: e.target.value }))}
              placeholder="Email content..."
              rows={4}
              data-testid="email-body-input"
            />
          </div>
          
          {/* Follow-up Task Section */}
          <Collapsible open={followUpOpen} onOpenChange={setFollowUpOpen}>
            <CollapsibleTrigger asChild>
              <Button type="button" variant="ghost" className="w-full justify-between p-2 h-auto">
                <div className="flex items-center gap-2">
                  <CheckSquare className="h-4 w-4" />
                  <span className="text-sm">Create Follow-up Task</span>
                </div>
                <ChevronDown className={`h-4 w-4 transition-transform ${followUpOpen ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 pt-2">
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="create-followup"
                  checked={createFollowUp}
                  onCheckedChange={setCreateFollowUp}
                />
                <Label htmlFor="create-followup" className="text-sm">Create a follow-up task</Label>
              </div>
              
              {createFollowUp && (
                <div className="space-y-3 pl-6 border-l-2 border-blue-200">
                  <Input
                    value={followUpDetails.title}
                    onChange={(e) => setFollowUpDetails(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Follow-up task title"
                    data-testid="followup-title-input"
                  />
                  <Input
                    type="date"
                    value={followUpDetails.due_date}
                    onChange={(e) => setFollowUpDetails(prev => ({ ...prev, due_date: e.target.value }))}
                    data-testid="followup-date-input"
                  />
                  
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Assignment Type</Label>
                    <Select 
                      value={followUpDetails.assigned_type}
                      onValueChange={(val) => setFollowUpDetails(prev => ({ ...prev, assigned_type: val, assigned_to: '' }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Assign to..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-blue-600" /> Team Member (Human)
                          </div>
                        </SelectItem>
                        <SelectItem value="ai_agent">
                          <div className="flex items-center gap-2">
                            <Bot className="h-4 w-4 text-purple-600" /> AI Agent
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {followUpDetails.assigned_type === 'user' ? (
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground flex items-center gap-1">
                        <User className="h-3 w-3" /> Select Team Member
                      </Label>
                      <Select 
                        value={followUpDetails.assigned_to}
                        onValueChange={(val) => setFollowUpDetails(prev => ({ ...prev, assigned_to: val }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose team member..." />
                        </SelectTrigger>
                        <SelectContent>
                          {teamMembers.map(member => (
                            <SelectItem key={member.id} value={member.id}>
                              {member.first_name} {member.last_name} {member.email ? `(${member.email})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground flex items-center gap-1">
                        <Bot className="h-3 w-3" /> Select AI Agent
                      </Label>
                      <Select 
                        value={followUpDetails.assigned_to}
                        onValueChange={(val) => setFollowUpDetails(prev => ({ ...prev, assigned_to: val }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose AI agent..." />
                        </SelectTrigger>
                        <SelectContent>
                          {aiAgents.map(agent => (
                            <SelectItem key={agent.id} value={agent.id}>
                              {agent.name || agent.display_name} - {agent.role_title || 'AI Assistant'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !form.subject.trim()} data-testid="log-email-submit">
              {loading ? 'Logging...' : 'Log Email'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});

LogEmailDialog.displayName = 'LogEmailDialog';

export default LogEmailDialog;
